import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { actionTypeName } from 'cio/utils/action-info';
import { readOnly, or } from '@ember/object/computed';
import { isEmpty, isNone, isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';
import { BeeTemplate } from 'cio/data/bee-templates';
import EditorService, { Editor, EmptyBeeTemplate } from 'cio/services/editor';
import LayoutModel from 'cio/models/layout';
import TemplateModel from 'cio/models/template';
import { ComposerCampaignActionTemplate, ComposerCampaignAction } from 'cio/utils/composer/composer-editable-entity';
import { ComposerActionTypeName } from 'cio/utils/composer';
import BeeService, { Template } from 'cio/services/bee';
import { tracked } from '@glimmer/tracking';
import { ComposerSection } from 'cio/components/action-composer';
import AuthService from 'cio/services/auth';
import { InAppStaticTemplate } from 'cio/services/in-app-static-templates';

export default class ComposerActionController extends Controller {
  queryParams = [
    {
      overview: { as: 'overview' },
      currentTab: { as: 'tab' },
    },
  ];

  @service auth!: AuthService;
  @service editor!: EditorService;
  @service bee!: BeeService;

  @tracked currentTab = ComposerSection.Editor;

  editableEntity!: ComposerCampaignActionTemplate | ComposerCampaignAction;

  actionTypeName: ComposerActionTypeName | null = null;
  selectedEditor: Editor | null = null;
  isShowingVisualEditorFeedbackForm = false;

  @computed('editableEntity.action.{id,templateId,isAttributeAction}')
  get saveModels() {
    if (
      (this.editableEntity.action.isAttributeAction &&
        !this.editableEntity.action.isCreateOrUpdatePersonAttributeAction) ||
      this.editableEntity.action.isCollectionAction
    ) {
      return [this.editableEntity.action];
    } else if (this.editableEntity.action.type === 'webhook_action') {
      return [this.editableEntity.action, this.editableEntity.template];
    } else {
      return [this.editableEntity.template];
    }
  }

  @computed('editorName')
  get isActionComposerV2() {
    return this.editorName === Editor.VISUAL && this.editableEntity.isEmail;
  }

  @computed('editableEntity.action.id')
  get headerNameModel() {
    if (this.editableEntity instanceof ComposerCampaignActionTemplate) {
      return this.editableEntity.defaultLanguageAction;
    }

    return this.editableEntity.action;
  }

  get hasReadOnlyPermission() {
    if (this.editableEntity.campaign.isApiTriggered) {
      return !this.auth.hasPermission('journeys/broadcasts/edit');
    }

    return !this.auth.hasPermission('journeys/campaigns/edit');
  }

  @or('editableEntity.template.editor', 'selectedEditor') editorName!: Editor;

  @or('isActionDirty', 'isTemplateDirty') isDirty!: boolean;
  @readOnly('editableEntity.template.hasDirtyAttributes') isTemplateDirty!: boolean;
  @tracked isActionDirty = false;

  @action
  save() {
    return Promise.all(this.saveModels.map((model) => model?.save()));
  }

  didSelectTemplateTask = task(async (template: TemplateModel | BeeTemplate | InAppStaticTemplate) => {
    if (template instanceof BeeTemplate) {
      // Bee templates' content (JSON and HTML) is fetched async, on demand.
      await template.fetchContent();
    }

    this.set('selectedEditor', template.editor);
    this.setEditor(template);
  });

  didSelectLayoutTask = task(async (layout: LayoutModel, editor: Editor) => {
    this.editableEntity.template?.setProperties({ editor, layout });
    this.setProperties({
      actionTypeName: this.editor.getEmailComposerForEditor(editor),
      selectedEditor: editor,
    });
  });

  @action
  startFromScratch(editor: Editor) {
    let template = this.editor.getEmptyTemplate(this.editableEntity.template as TemplateModel, editor);
    this.set('selectedEditor', editor);
    this.setEditor(template);
  }

  setEditor(baseTemplate?: TemplateModel | BeeTemplate | EmptyBeeTemplate | InAppStaticTemplate) {
    if (this.editableEntity.isEmail) {
      // For existing emails as the base template, use the body, bodyJson, bodyAmp
      // layout from the base template and set the right editor type.
      let editor = !isEmpty(baseTemplate?.editor) ? baseTemplate?.editor : this.editorName;
      let template = this.editableEntity.template as TemplateModel;

      if (isEmpty(editor)) {
        // If there is no editor selected, check whether there is a persisted
        // Bee state for this template. It's possible a customer encountered Bee
        // errors without having saved the editor selection, and then clicked
        // "Save and reload". In this case, we need to make sure to go straight
        // to the drag-and-drop composer.
        // The composer then will take care of restoring the template content
        // from localStorage.
        let persistedJson = this.bee.getPersistedStateFor(template as Template);
        if (!isNone(persistedJson)) {
          editor = Editor.DRAG_AND_DROP;
          template.set('editor', Editor.DRAG_AND_DROP);
        }
      }

      let propertyKeys: (keyof TemplateModel)[] = ['body', 'bodyJson', 'layout'];
      if (baseTemplate instanceof TemplateModel) {
        propertyKeys.push('bodyAmp');
      }

      propertyKeys.forEach((key) => {
        // casting to `any` to avoid impossible type error around accessing bodyAmp on BeeTemplate | EmptyBeeTemplate
        let currentValue = (baseTemplate as any)[key];

        if (isPresent(currentValue)) {
          // "layout" is DS.PromiseObject, detect it and pass only its resolved ".content":
          let isPromiseObject = Object.prototype.hasOwnProperty.call(currentValue, 'content');
          template.set(key, isPromiseObject ? currentValue.content : currentValue);
        }
      });

      this.set('actionTypeName', this.editor.getEmailComposerForEditor(editor as Editor));
    } else if (this.editableEntity.isInApp) {
      if (baseTemplate instanceof TemplateModel) {
        let propertyKeys: (keyof TemplateModel)[] = ['body', 'bodyJson', 'recipient'];

        propertyKeys.forEach((key) => {
          let currentValue = baseTemplate[key];
          if (isPresent(currentValue)) {
            this.editableEntity.template?.set(key, currentValue);
          }
        });
      } else if (baseTemplate instanceof InAppStaticTemplate) {
        this.editableEntity.template?.set(
          'bodyJson',
          JSON.stringify({
            carta_content: baseTemplate.body,
          }),
        );
      }

      this.set('actionTypeName', this.editor.getInAppComposerForEditor(this.editorName as Editor));
    } else {
      // For all other editor types: push, slack, twilio, etc.
      this.set('actionTypeName', actionTypeName(this.editableEntity.action.type));
    }
  }

  @action
  onDirtyChange(isDirty: boolean) {
    if (this.isActionDirty === isDirty) {
      return;
    }
    this.isActionDirty = isDirty;
  }
}
